import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Spin, Alert, DatePicker, Select, Button, Space } from 'antd';
import {
  DashboardService,
  ProcessSummary,
  DashboardFilters,
  DashboardMetrics,
} from '../../services/api/dashboardService';
import { ProcessTypeSummaryCards } from './ProcessTypeSummaryCards';
import { ProcessDetailTable } from './ProcessDetailTable';
import { DashboardMetricsDisplay } from './DashboardMetricsDisplay';
import { subDays } from 'date-fns';
import dayjs from 'dayjs';
import './ArtifactItemDashboard.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

export const ArtifactItemDashboard: React.FC = () => {
  const [processSummary, setProcessSummary] = useState<ProcessSummary[]>([]);
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics | null>(null);
  const [availableProcessTypes, setAvailableProcessTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [selectedProcessTypes, setSelectedProcessTypes] = useState<string[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Date, Date]>([subDays(new Date(), 30), new Date()]);
  const [selectedProcessType, setSelectedProcessType] = useState<string | null>(null);

  // Load available process types on component mount
  useEffect(() => {
    const loadProcessTypes = async () => {
      try {
        const types = await DashboardService.getAvailableProcessTypes();
        setAvailableProcessTypes(types);
      } catch (err) {
        console.error('Failed to load process types:', err);
      }
    };
    void loadProcessTypes();
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: DashboardFilters = {
        process_types: selectedProcessTypes.length > 0 ? selectedProcessTypes : undefined,
        process_ids: selectedProcesses.length > 0 ? selectedProcesses : undefined,
        date_from: dateRange[0],
        date_to: dateRange[1],
      };

      // Fetch process summary and dashboard metrics in parallel
      const [summaryData, metricsData] = await Promise.all([
        DashboardService.getProcessSummary(filters),
        DashboardService.getDashboardMetrics(filters),
      ]);

      setProcessSummary(summaryData);
      setDashboardMetrics(metricsData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedProcessTypes, selectedProcesses, dateRange]);

  // Fetch data when filters change
  useEffect(() => {
    void fetchDashboardData();
  }, [fetchDashboardData]);

  const handleProcessTypeFilterChange = (values: string[]) => {
    setSelectedProcessTypes(values);
    setSelectedProcessType(null); // Reset selected process type when filter changes
  };

  const handleDateRangeChange = (dates: unknown) => {
    if (dates && Array.isArray(dates) && dates.length === 2) {
      const dateArray = dates as { toDate: () => Date }[];
      setDateRange([dateArray[0].toDate(), dateArray[1].toDate()]);
    }
  };

  const handleProcessTypeSelect = (processType: string) => {
    setSelectedProcessType(processType);
  };

  const handleRefresh = () => {
    void fetchDashboardData();
  };

  if (loading && processSummary.length === 0) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="artifact-item-dashboard">
      <div className="dashboard-header">
        <h1>Artifact Item Dashboard</h1>
        <p>Monitor artifact items across all processes</p>
      </div>

      {/* Dashboard Filters */}
      <Card className="dashboard-filters" title="Filters">
        <Row gutter={16} align="middle">
          <Col span={6}>
            <label>Process Types:</label>
            <Select
              mode="multiple"
              placeholder="Select process types"
              value={selectedProcessTypes}
              onChange={handleProcessTypeFilterChange}
              style={{ width: '100%' }}
              allowClear
            >
              {availableProcessTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <label>Date Range:</label>
            <RangePicker
              value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
              onChange={handleDateRangeChange}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={6}>
            <Space>
              <Button type="primary" onClick={handleRefresh}>
                Refresh
              </Button>
              <Button
                onClick={() => {
                  setSelectedProcessTypes([]);
                  setSelectedProcesses([]);
                  setDateRange([subDays(new Date(), 30), new Date()]);
                }}
              >
                Clear Filters
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Dashboard Metrics */}
      {dashboardMetrics && <DashboardMetricsDisplay metrics={dashboardMetrics} />}

      {/* Process Type Summary Cards */}
      <ProcessTypeSummaryCards
        processSummary={processSummary}
        onProcessTypeSelect={handleProcessTypeSelect}
        selectedProcessType={selectedProcessType}
      />

      {/* Process Detail Table */}
      {selectedProcessType && (
        <ProcessDetailTable
          processType={selectedProcessType}
          dateFrom={dateRange[0]}
          dateTo={dateRange[1]}
        />
      )}

      {/* Loading indicator for data updates */}
      {loading && processSummary.length > 0 && (
        <div className="dashboard-updating">
          <Spin size="small" />
          <span>Updating data...</span>
        </div>
      )}
    </div>
  );
};
